import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { Button } from './ui/button';

/**
 * Centered modal for destructive confirms. Built on the existing
 * Sheet/Dialog primitive (Radix Dialog) — same overlay, same focus
 * trap, same Escape/overlay-click semantics — but always centered
 * regardless of viewport, since a "did you mean to do this?" prompt
 * should stop the user dead, not slide in like content.
 *
 * Async-aware: while `onConfirm` is pending, the confirm button
 * shows the busy label and Cancel is disabled, so the user can't
 * spawn duplicate destructive operations.
 */
export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  busyLabel?: string;
  cancelLabel?: string;
  /** Use 'destructive' for delete/abandon actions (default). */
  tone?: 'destructive' | 'primary';
  /**
   * Called when the user confirms. May be async — the dialog stays
   * open and disables both buttons until the promise settles. The
   * dialog auto-closes on success; errors propagate to the caller
   * (which is responsible for surfacing them).
   */
  onConfirm: () => void | Promise<void>;
  /** Pinned `data-testid` on the confirm button for e2e. */
  testId?: string;
}

export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  busyLabel = 'Working…',
  cancelLabel = 'Cancel',
  tone = 'destructive',
  onConfirm,
  testId,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);

  async function handleConfirm() {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      // Reset on next mount (open=false unmounts content, so this
      // reset only matters if the caller keeps the dialog open after
      // an error and the user retries).
      setBusy(false);
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (busy) return; // Ignore overlay/Escape while in flight.
        onOpenChange(next);
      }}
    >
      <SheetContent side="center" className="max-w-md">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          {description ? <SheetDescription>{description}</SheetDescription> : null}
        </SheetHeader>
        <SheetFooter className="pt-6">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="h-11"
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={tone === 'destructive' ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={busy}
            className="h-11"
            data-testid={testId}
          >
            {busy ? busyLabel : confirmLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
